import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { add } from 'date-fns';
import { WIZARD_STATUS_COMPLETE } from 'platform/site-wide/wizard';

import SubTaskContainerHeader from '../SubTaskContainerHeader';
import useSubtaskStatusMarker from '../useSubtaskStatusMarker';
import { useSyncFormBddState } from '../bddFormData';
import { getDiffInDays } from '../utils';
import { usePathHistory } from '../utils/tracking/PathHistoryContext';
import {
  trackScreenerStepView,
  trackBddEligibilityDisplayed,
  trackExternalLinkClick,
  trackScreenerCompleted,
} from '../../utils/tracking/features/screenerTracking';
import { formatDate } from '../../../shared/utils/dates';
import { RESUME_PAGE } from '../../constants';
import { SHA_PART_A_PDF_URL } from '../../../shared/constants/entryConstants';
import pageNames from './pageNames';

const militaryStatusKey = 'view:militaryStatus';
const claimTypeKey = 'view:claimType';
const pactDataKey = 'view:pactEligibility';

/**
 * File BDD — shown when the veteran's separation date qualifies for the
 * Benefits Delivery at Discharge (BDD) program (90–180 days out). Continue
 * hands off directly into Form 526EZ through the save-in-progress resume route.
 * @param {Object} data - subtask data
 * @returns {JSX}
 */
const FileBddPage = ({ data = {} }) => {
  const { radDate } = data;
  const { addStep, getHistory } = usePathHistory();

  // Completing the screener lets the resume route load the form on Continue.
  useSubtaskStatusMarker(WIZARD_STATUS_COMPLETE);

  // The form consumes the saved separation date the first time it opens, so a
  // Veteran coming back from the form needs it saved again.
  useSyncFormBddState(data, true);

  const daysUntilSeparation = getDiffInDays(radDate) + 1;
  const daysRemainingToFileBDD = daysUntilSeparation - 90;
  const isLastDayToFileBDD = daysRemainingToFileBDD === 0;
  const deadlineDate = formatDate(
    add(new Date(), { days: daysRemainingToFileBDD }),
  );
  const daysLabel = daysRemainingToFileBDD === 1 ? 'day' : 'days';

  useEffect(() => {
    trackScreenerStepView({
      stepName: pageNames.fileBdd,
      pathHistoryContext: { addStep, getHistory },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    trackBddEligibilityDisplayed({
      daysRemaining: daysRemainingToFileBDD,
      isLastDay: isLastDayToFileBDD,
    });
  }, [daysRemainingToFileBDD, isLastDayToFileBDD]);

  return (
    <div id={pageNames.fileBdd}>
      <SubTaskContainerHeader title="File a Benefits Delivery at Discharge (BDD) claim" />
      <va-alert status="warning" class="vads-u-margin-bottom--3">
        <h3 slot="headline">
          You must submit a Separation Health Assessment with your claim
        </h3>
        <p className="vads-u-margin-top--0">
          To be considered for the BDD program, you must submit a Separation
          Health Assessment (self-assessment, also called “Part A”) with your
          disability compensation claim.
        </p>
        <va-link
          external
          href={SHA_PART_A_PDF_URL}
          text="Download your Separation Health Assessment"
          onClick={() =>
            trackExternalLinkClick({
              linkLabel: 'Download your Separation Health Assessment',
              pageName: pageNames.fileBdd,
              pathHistoryData: getHistory(),
            })
          }
        />
      </va-alert>
      <p>
        Based on your separation date,{' '}
        {isLastDayToFileBDD ? (
          <>
            <strong>today is your last day</strong> to file a BDD claim
          </>
        ) : (
          <>
            you have {daysRemainingToFileBDD} {daysLabel} left to file a BDD
            claim
          </>
        )}{' '}
        using VA Form 21-526EZ. Your deadline is{' '}
        <strong>{deadlineDate} at 11:59 p.m. ET</strong>.
      </p>
      <p>
        If you miss this deadline, you can still file for disability
        compensation. Your claim will be processed as a standard claim instead.
      </p>
      <p>
        Next, you’ll fill out your application. You’ll have the opportunity to
        share information about yourself and your service.
      </p>
      <p>
        We’ll ask you to describe the conditions you’re claiming, include
        supporting evidence, and review everything before submitting.
      </p>
    </div>
  );
};

FileBddPage.propTypes = {
  data: PropTypes.shape({}),
};

export default {
  name: pageNames.fileBdd,
  component: FileBddPage,
  back: pageNames.releaseDate,
  // Route the veteran straight into Form 526EZ through the platform
  // save-in-progress resume route (basename-relative; see RESUME_PAGE).
  next: () => RESUME_PAGE,
  onContinue: data =>
    trackScreenerCompleted({
      pageName: pageNames.fileBdd,
      outcome: 'bdd',
      militaryStatus: data[militaryStatusKey],
      claimType: data[claimTypeKey],
      pactEligibility: data[pactDataKey],
    }),
};
